import { useEffect, useMemo, useState } from 'react';
import { Monitor, Tablet, Smartphone, RotateCw, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/cn';

interface LivePreviewProps {
  themeSlug: string;
  frontendUrl: string;
  path?: string;
  colors: Record<string, string>;
  typography: Record<string, string>;
  layout: Record<string, string>;
  packOptions: Record<string, string>;
}

const VIEWPORTS = { desktop: '100%', tablet: '820px', mobile: '390px' } as const;

/**
 * Live preview of the real site rendered with the edited theme. All unsaved
 * edits (colors, fonts, layout, pack options) are passed as query params so the
 * preview route applies them — WordPress-customizer style.
 */
export function LivePreview({ themeSlug, frontendUrl, path = '', colors, typography, layout, packOptions }: LivePreviewProps) {
  const [vp, setVp] = useState<keyof typeof VIEWPORTS>('desktop');
  const [nonce, setNonce] = useState(0);

  const params = useMemo(() => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(colors)) if (v) qs.set(`c_${k}`, v);
    for (const [k, v] of Object.entries(typography)) if (v) qs.set(`f_${k}`, v);
    for (const [k, v] of Object.entries(layout)) if (v) qs.set(`l_${k}`, v);
    for (const [k, v] of Object.entries(packOptions)) if (v) qs.set(`opt_${k}`, v);
    return qs.toString();
  }, [colors, typography, layout, packOptions]);

  // Debounce so dragging a color picker doesn't reload the iframe constantly.
  const [debounced, setDebounced] = useState(params);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(params), 400);
    return () => clearTimeout(t);
  }, [params]);

  const src = `${frontendUrl}/preview/${themeSlug}${path ? `/${path}` : ''}?${debounced}&_=${nonce}`;
  const openSrc = `${frontendUrl}/preview/${themeSlug}${path ? `/${path}` : ''}?${debounced}`;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-neutral-500">Sitio real · cambios sin guardar incluidos</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setNonce((n) => n + 1)}
            className="grid size-8 place-items-center rounded-md border border-neutral-200 text-neutral-600 hover:bg-neutral-100"
            title="Recargar"
          >
            <RotateCw className="size-3.5" />
          </button>
          <a
            href={openSrc}
            target="_blank"
            rel="noopener noreferrer"
            className="grid size-8 place-items-center rounded-md border border-neutral-200 text-neutral-600 hover:bg-neutral-100"
            title="Abrir en pestaña"
          >
            <ExternalLink className="size-3.5" />
          </a>
          <div className="flex items-center rounded-md border border-neutral-200">
            <VpBtn active={vp === 'desktop'} onClick={() => setVp('desktop')}><Monitor className="size-3.5" /></VpBtn>
            <VpBtn active={vp === 'tablet'} onClick={() => setVp('tablet')}><Tablet className="size-3.5" /></VpBtn>
            <VpBtn active={vp === 'mobile'} onClick={() => setVp('mobile')}><Smartphone className="size-3.5" /></VpBtn>
          </div>
        </div>
      </div>
      <div className="overflow-auto rounded-lg border border-neutral-200 bg-neutral-100 p-2">
        <iframe
          key={`${vp}`}
          src={src}
          title="Vista previa en vivo"
          className="mx-auto block h-[640px] border-0 bg-white shadow-sm transition-all"
          style={{ width: VIEWPORTS[vp] }}
        />
      </div>
    </div>
  );
}

function VpBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'grid size-8 place-items-center transition-colors first:rounded-l-md last:rounded-r-md',
        active ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:bg-neutral-100',
      )}
    >
      {children}
    </button>
  );
}
